/**
 * API methods for Broadcast Queue (очередь рассылок с ручным подтверждением)
 * Дата: 13.02.2026
 * Обновлено: 14.02.2026 — добавлена поддержка tenant_id, delivery preview
 * Обновлено: 10.04.26 — approve-all теперь стартует async job и читает progress по operation job endpoint
 * Связан с: internal/handler/broadcast_queue.go, internal/handler/broadcast_queue_actions.go
 */
import { apiClient } from './client';
import type {
  BroadcastQueueItem,
  BroadcastQueueListResponse,
  BroadcastQueueSummary,
  BroadcastQueueFilter,
  BroadcastModeResponse,
  DeliveryPreview,
  BatchPreviewResponse,
  ChangeChannelResponse,
  AvailableChannelsResponse,
  BulkChangeChannelResponse,
  SendTestCloneResponse,
  StartBroadcastOperationJobResponse,
  BroadcastOperationJob,
} from '@/types/broadcastQueue';

export const broadcastQueueApi = {
  /** Список очереди с фильтрацией */
  list: async (filters: BroadcastQueueFilter & { tenant_id?: string } = {}): Promise<BroadcastQueueListResponse> => {
    const params: Record<string, string> = {};
    if (filters.tenant_id) params.tenant_id = filters.tenant_id;
    if (filters.status) params.status = filters.status;
    if (filters.event_type) params.event_type = filters.event_type;
    if (filters.phone) params.phone = filters.phone;
    if (filters.created_from) params.created_from = filters.created_from;
    if (filters.created_to) params.created_to = filters.created_to;
    if (filters.event_date_from) params.event_date_from = filters.event_date_from;
    if (filters.event_date_to) params.event_date_to = filters.event_date_to;
    if (filters.semantic_dates) params.semantic_dates = 'true';
    if (filters.offset !== undefined) params.offset = String(filters.offset);
    if (filters.limit !== undefined) params.limit = String(filters.limit);

    const response = await apiClient.get<BroadcastQueueListResponse>('/broadcast-queue', { params });
    return response.data;
  },

  /** Агрегированная статистика */
  summary: async (
    tenantId?: string,
    filters: Pick<
      BroadcastQueueFilter,
      'status' | 'event_type' | 'phone' | 'created_from' | 'created_to' | 'event_date_from' | 'event_date_to' | 'semantic_dates'
    > = {},
  ): Promise<BroadcastQueueSummary> => {
    // 27.03.26: summary должен принимать те же фильтры, что и таблица, иначе карточки расходятся со списком.
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    if (filters.status) params.status = filters.status;
    if (filters.event_type) params.event_type = filters.event_type;
    if (filters.phone) params.phone = filters.phone;
    if (filters.created_from) params.created_from = filters.created_from;
    if (filters.created_to) params.created_to = filters.created_to;
    if (filters.event_date_from) params.event_date_from = filters.event_date_from;
    if (filters.event_date_to) params.event_date_to = filters.event_date_to;
    if (filters.semantic_dates) params.semantic_dates = 'true';
    const response = await apiClient.get<BroadcastQueueSummary>('/broadcast-queue/summary', { params });
    return response.data;
  },

  /** Получить элемент по ID */
  getById: async (id: string): Promise<BroadcastQueueItem> => {
    const response = await apiClient.get<BroadcastQueueItem>(`/broadcast-queue/${id}`);
    return response.data;
  },

  /** Одобрить выбранные (и отправить) */
  approve: async (ids: string[], tenantId?: string): Promise<{ approved: number; sent: number; failed: number; message: string }> => {
    const response = await apiClient.post('/broadcast-queue/approve', { ids, tenant_id: tenantId });
    return response.data;
  },

  /** Одобрить ВСЕ pending */
  approveAll: async (tenantId?: string): Promise<StartBroadcastOperationJobResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post<StartBroadcastOperationJobResponse>('/broadcast-queue/approve-all', {}, { params });
    return response.data;
  },

  getOperationJob: async (jobId: string, tenantId?: string): Promise<BroadcastOperationJob> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get<BroadcastOperationJob>(`/broadcast-queue/operations/${jobId}`, { params });
    return response.data;
  },

  /** Отклонить выбранные */
  reject: async (ids: string[], reason?: string): Promise<{ rejected: number; message: string }> => {
    const response = await apiClient.post('/broadcast-queue/reject', { ids, reason });
    return response.data;
  },

  /** Очистить все pending (отклонить все) */
  clear: async (tenantId?: string): Promise<{ cleared: number; message: string }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post('/broadcast-queue/clear', {}, { params });
    return response.data;
  },

  /** Получить текущий режим рассылок */
  getMode: async (tenantId?: string): Promise<BroadcastModeResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get<BroadcastModeResponse>('/broadcast-queue/mode', { params });
    return response.data;
  },

  /** Переключить режим (superadmin only) */
  setMode: async (mode: 'manual' | 'auto', tenantId?: string): Promise<BroadcastModeResponse & { message: string }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post('/broadcast-queue/mode', { mode }, { params });
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Delivery Preview (14.02.2026)
  // ═══════════════════════════════════════════════════════════════════════

  /** Предпросмотр доставки для одного элемента */
  getDeliveryPreview: async (id: string, tenantId?: string): Promise<DeliveryPreview> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get<DeliveryPreview>(`/broadcast-queue/${id}/delivery-preview`, { params });
    return response.data;
  },

  /** Массовый предпросмотр доставки */
  batchDeliveryPreview: async (ids: string[], tenantId?: string): Promise<BatchPreviewResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post<BatchPreviewResponse>('/broadcast-queue/batch-preview', { ids }, { params });
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Channel Switch (12.03.2026)
  // ═══════════════════════════════════════════════════════════════════════

  /** Сменить канал доставки для элемента */
  changeChannel: async (id: string, channel: string, tenantId?: string): Promise<ChangeChannelResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post<ChangeChannelResponse>(`/broadcast-queue/${id}/channel`, { channel }, { params });
    return response.data;
  },

  /** Получить список доступных каналов для элемента */
  getAvailableChannels: async (id: string, tenantId?: string): Promise<AvailableChannelsResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get<AvailableChannelsResponse>(`/broadcast-queue/${id}/channels`, { params });
    return response.data;
  },

  /** Массовая смена канала для выбранных элементов */
  bulkChangeChannel: async (ids: string[], channel: string, tenantId?: string): Promise<BulkChangeChannelResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post<BulkChangeChannelResponse>('/broadcast-queue/bulk-channel', { ids, channel }, { params });
    return response.data;
  },

  /** Создать и сразу отправить контрольную копию себе */
  sendTestClone: async (
    id: string,
    tenantId?: string,
    mode: 'snapshot' | 'deep_emulation' | 'channel_matrix' = 'snapshot',
    channels?: string[],
  ): Promise<SendTestCloneResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.post<SendTestCloneResponse>(`/broadcast-queue/${id}/send-test`, { mode, channels }, { params });
    return response.data;
  },
};
